import React from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';

import { FilterProductIcon } from '../../../../../common/ui/filters/filter-product-icon';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import SelectFilter from '../../../../../common/ui/filters/select-filter';
import { formatAvatarOptionLabel } from '../../../../../common/ui/filters/select-filter/option-label';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { useFiltersDiscoverability2Enabled, usePrimaryProduct } from '../../../../../controllers/store/bootstrap';
import { filter as projectFilter } from '../../../../../controllers/store/search/filters/project';

import { i18n } from './messages';

const ProjectFilter = ({
	isFilterInDropdown,
	showPrimaryProductIcon,
	fit = 'default',
	onFilterChange,
	hideOnHoverOut,
}: FilterProps): React.JSX.Element | null => {
	const [primaryProduct] = usePrimaryProduct();
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [
		filterData,
		{ loadInitialOptions, setLookupInput, setOption, clearLookupInput, clearOptions },
	] = useSelectFilter({
		filterId: projectFilter.id,
	});

	if (!filterData) {
		return null;
	}

	const {
		isLoading,
		suggestedOptions,
		selectedOptions,
		options,
		isLookupDefined,
		lookupInput,
		isLookupActive,
	} = filterData;

	const groupedOptions = [
		{
			label: formatMessage(
				fg('jira_project_rename_to_spaces')
					? i18n.jiraFilterSpacesLabelSelected
					: i18n.jiraFilterProjectLabelSelected,
			),
			options: selectedOptions,
		},
		{
			label: formatMessage(
				fg('jira_project_rename_to_spaces')
					? i18n.jiraFilterSpacesLabelUnselected
					: i18n.jiraFilterProjectLabelUnselected,
			),
			options: suggestedOptions,
		},
	];

	const label =
		fit === 'compact' && selectedOptions.length > 0
			? ''
			: formatMessage(fg('jira_project_rename_to_spaces') ? i18n.spaces : i18n.project);

	return (
		<SelectFilter
			contentFit={fit}
			canClearFilter
			checkboxIndicators
			onDropdownOpen={loadInitialOptions}
			onDropdownClose={clearLookupInput}
			onSearchInputChange={setLookupInput}
			onSelectChange={(option, ...restArgs) => {
				setOption(option, ...restArgs);
				onFilterChange?.(option);
			}}
			options={isLookupActive ? options : groupedOptions}
			selectedOptions={selectedOptions}
			label={label}
			placeholder={formatMessage(
				fg('jira_project_rename_to_spaces')
					? i18n.jiraFilterSpacesPlaceholder
					: i18n.jiraFilterProjectPlaceholder,
			)}
			filterOrControlId={'project'}
			isLoading={isLoading}
			isSearchable={isLookupDefined}
			searchInput={lookupInput}
			formatOptionLabel={formatAvatarOptionLabel}
			onClearOptions={clearOptions}
			isFilterInDropdown={isFilterInDropdown}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			iconBefore={
				<FilterProductIcon
					showPrimaryProductIcon={showPrimaryProductIcon}
					primaryProduct={primaryProduct}
				/>
			}
			hideOnHoverOut={hideOnHoverOut}
		/>
	);
};

export default ProjectFilter;
